/**
 * The person beside the plan, as the owner's prototype draws it (person-panel-profile.prototype.html,
 * 2026-10-28): the header with the ways to reach the person, the week's hours against the agreed week,
 * a waiting request, the rest of the week one day per row, «Mer om uka» folded, and one action in the
 * footer that follows from the week. «Åpne profil» turns the panel to the person's facts. Every figure
 * is one the page already holds or an existing route sends; nothing new is asked of the server.
 */
import {
  type ApiAbsence,
  type ApiAvailability,
  type ApiOffer,
  type ApiShift,
  type ApiSwap,
  type ApiUser,
  useShiftsPlain,
  useTimeBank,
  useUser,
} from '@/api/data';
import { T, useT } from '@/i18n';
import { type Entry, dayEntries, hours, oslo, ruleText, tenantRules, weekOf } from '@/rules';
import { DN, DNL, avTone, fmtDs, fmtPhone, fullName, initials, num, shiftSpan } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { type Person, addDays, ageOn, ceilings, dow, inRange, isoWeek } from '@timefly/core';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { setupItems } from '../ansatt/roster';
import { CALLS, CallButton, MessageButton } from './Contact';
import css from './PersonPanel.module.css';
import { usePhone } from './Phone';
import { type Avail, availOn, dateOf, typeLabel } from './model';

const r1 = (n: number) => Math.round(n * 10) / 10;
/** The adult daily rest, AML §10-8 (1); core's checkShift uses the same 11 h. */
const REST = 11;

/** One day of the week: the person's shifts, else an absence, else what their availability says. */
export interface StripDay {
  date: string;
  shifts: ApiShift[];
  absence: ApiAbsence | null;
  avail: Avail | null;
}

/** The rest before each shift, from the end of the person's shift before it, in hours to a tenth. */
export function restBefore(shifts: ApiShift[]): Map<number, number> {
  const sorted = [...shifts].sort((a, b) => Date.parse(a.start_time) - Date.parse(b.start_time));
  const out = new Map<number, number>();
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1] as ApiShift;
    const next = sorted[i] as ApiShift;
    const h = (Date.parse(next.start_time) - Date.parse(prev.end_time)) / 36e5;
    if (h >= 0) out.set(next.id, r1(h));
  }
  return out;
}

/** A pending request on the person, with what the page can do about it. */
export interface PersonRequests {
  offers: ApiOffer[];
  swaps: ApiSwap[];
}

type Note = { text: string; sev: 'd' | 'w'; youth: boolean };

export function PersonPanel({
  user,
  person,
  week,
  hours: shownHours,
  loaded,
  shifts,
  absences,
  avail,
  fair,
  requests,
  canPay,
  canMessage,
  canEdit = false,
  editable,
  today,
  onShift,
  onAbsence,
  onRule,
  onNew,
  onOnly,
  onClose,
}: {
  user: ApiUser;
  /** the rule engine's person for this row */
  person: Person;
  /** the Monday of the week the panel shows */
  week: string;
  /** the week's hours as the row beside it counts them (Plan or Faktisk); computed here when not given */
  hours?: number | undefined;
  /** the dates the page's own read covers; a week outside it is read here */
  loaded: { from: string; to: string };
  /** this person's shifts the page holds */
  shifts: ApiShift[];
  /** this person's absences, any status */
  absences: ApiAbsence[];
  /** the company's availability rows (the plan's own read) */
  avail: ApiAvailability[];
  /** evenings and weekends over four weeks against the team's average; null where the page has not read them */
  fair: { eve: number; wkd: number; avgEve: number; avgWkd: number } | null;
  requests: PersonRequests;
  /** pay.view: the time bank and the rates may be read */
  canPay: boolean;
  /** schedule.manage: «Melding» through Timefly (Contact.tsx) */
  canMessage: boolean;
  /** people.manage: «Rediger» and «Legg til» lead to the employee's edit drawer */
  canEdit?: boolean;
  /** the plan may be changed: the «+» and «Planlegg» */
  editable: boolean;
  today: string;
  onShift: (s: ApiShift) => void;
  onAbsence: (a: ApiAbsence) => void;
  onRule: (e: Entry) => void;
  onNew: (date: string) => void;
  onOnly: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const [view, setView] = useState<'week' | 'profile'>('week');
  const head = useRef<HTMLHeadingElement | null>(null);
  const weekHead = useRef<HTMLHeadingElement | null>(null);
  const profHead = useRef<HTMLHeadingElement | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: another person is a new heading to move to
  useEffect(() => {
    setView('week');
    head.current?.focus({ preventScroll: true });
  }, [user.id]);
  useEffect(() => {
    if (view === 'profile') profHead.current?.focus({ preventScroll: true });
  }, [view]);
  const touch = usePhone(CALLS);
  const full = useUser(user.id).data;
  const bank = useTimeBank(canPay ? user.id : null).data;
  const sun = addDays(week, 6);
  // the day view holds one day: the week is read here then, from the same route as the plan
  const outside = week < loaded.from || sun > loaded.to;
  const extra = useShiftsPlain(addDays(week, -1), addDays(week, 7), outside).data;
  const mine = outside ? (extra?.shifts ?? []).filter((s) => s.user_id === user.id) : shifts;
  const birth = full?.birth_date ?? user.birth_date ?? undefined;
  const who: Person = { ...person, birthDate: birth ?? undefined };
  const age = ageOn(birth ?? undefined, today);
  const young = age != null && age < 18;
  const cap = ceilings(who, today);
  const rules = tenantRules();
  const wk0 = weekOf(who, week, mine, rules);
  const wk = { ...wk0, hours: shownHours ?? wk0.hours };
  const inWeek = mine
    .filter((s) => inRange(dateOf(s), week, sun))
    .sort((a, b) => Date.parse(a.start_time) - Date.parse(b.start_time));
  const rest = restBefore(mine);
  /** what the row says about a shift: a short rest first, else the worst rule entry on it */
  const noteOf = (s: ApiShift): Note | null => {
    const gap = rest.get(s.id);
    if (gap != null && gap < REST)
      return { text: T('{h} t hvile, krever {n} t', { h: num(gap), n: REST }), sev: 'd', youth: false };
    const es = dayEntries(s, who, rules).filter((e) => e.code !== 'rest');
    const e = es.find((x) => x.sev === 'd') ?? es[0];
    if (!e) return null;
    const youth = e.code.startsWith('youth_');
    const text =
      e.code === 'youth_rest'
        ? T('{h} t hvile, krever {n} t', { h: num(Number(e.args.hours)), n: num(Number(e.args.max)) })
        : ruleText(t, e);
    return { text, sev: e.sev, youth };
  };
  const strip: StripDay[] = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(week, i);
    return {
      date: d,
      shifts: inWeek.filter((s) => dateOf(s) === d),
      absence:
        absences.find(
          (a) =>
            a.status !== 'declined' &&
            inRange(d, oslo(a.start_date)?.date ?? a.start_date, oslo(a.end_date)?.date ?? a.end_date),
        ) ?? null,
      avail: availOn(avail, user.id, d),
    };
  });
  // the requests waiting on the manager, on this person's shifts
  const offers = requests.offers.filter((o) => o.offered_by === user.id);
  const swaps = requests.swaps.filter((s) => s.offered_by_one === user.id || s.offered_by_two === user.id);
  const asks = [
    ...swaps.map((x) => {
      const mineOne = x.offered_by_one === user.id;
      return {
        key: `s${x.id}`,
        swap: true,
        shift: mineOne ? x.shiftOne : x.shiftTwo,
        other: mineOne ? x.offeredByUserTwo : x.offeredByUserOne,
      };
    }),
    ...offers.map((x) => ({ key: `o${x.id}`, swap: false, shift: x.shift, other: x.acceptedByUser })),
  ].filter((x) => x.shift);
  const askOn = (s: ApiShift) => asks.find((a) => a.shift?.id === s.id);
  // the page's own copy of the shift where it holds one, so the shift panel opens what the plan shows
  const openShift = (s: ApiShift) => onShift(mine.find((x) => x.id === s.id) ?? s);

  const phone = (full?.phone ?? user.phone ?? '').replace(/\s/g, '');
  const mail = full?.email ?? user.email ?? '';
  const teams = (user.teams ?? []).map((x) => x.name).join(', ');
  const role = [user.title || T('Medarbeider'), teams].filter(Boolean).join(', ');
  const vac = full?.vacation_days_balance;
  const vacOf = full?.total_vacation_days_per_year;
  const year = wk.entries.find((e) => e.code === 'year_over_200');
  const edit = (field: string) => `/ansatte/${user.id}?rediger=${field}`;

  // the week: today onwards in rows, the days before folded
  const inWk = inRange(today, week, sun);
  const split = inWk ? strip.findIndex((x) => x.date === today) : 0;
  const ahead = strip.slice(split);
  const past = strip.slice(0, split);
  const pastLabel =
    past.length === 1
      ? T('Vis {a}', { a: DNL[0] as string })
      : past.length === 2
        ? T('Vis {a} og {b}', { a: DNL[0] as string, b: DNL[1] as string })
        : T('Vis {a} til {b}', { a: DNL[0] as string, b: DNL[past.length - 1] as string });

  // the one action the footer offers: a broken rule on a shift, a youth limit, else the next free day
  const coming = strip.filter((x) => x.date >= today);
  const notes = coming.flatMap((x) => x.shifts.map((s) => ({ s, n: noteOf(s), d: x.date })));
  const broken = notes.find((x) => x.n?.sev === 'd' && !x.n.youth);
  const youthHit = young ? notes.find((x) => x.n?.youth) : undefined;
  const freeDay = coming.find((x) => !x.shifts.length && !x.absence && x.avail?.kind !== 'unavail');
  const nextFree =
    coming.find((x) => x.avail?.kind === 'partly' && !x.shifts.length && !x.absence) ?? freeDay;
  const primary = broken
    ? {
        label: T('Rett {dag}svakten', { dag: DNL[dow(broken.d)] as string }),
        icon: 'arrowRight' as const,
        go: () => openShift(broken.s),
      }
    : youthHit
      ? {
          label: T('Endre {dag}svakten', { dag: DNL[dow(youthHit.d)] as string }),
          icon: 'arrowRight' as const,
          go: () => openShift(youthHit.s),
        }
      : editable && nextFree
        ? {
            label: T('Planlegg {dag}', { dag: DNL[dow(nextFree.date)] as string }),
            icon: 'plus' as const,
            go: () => onNew(nextFree.date),
            plan: true,
          }
        : null;
  const toWeek = () => {
    weekHead.current?.scrollIntoView({ block: 'start' });
    weekHead.current?.focus({ preventScroll: true });
  };

  const over = wk.norm != null && wk.hours > wk.norm + 0.05;
  // more than 8 t under the agreed week is amber too, as in the name column (#272 A-V03)
  const under = wk.norm != null && wk.hours < wk.norm - 8;
  // red only past a legal limit, as the prototype: under 18 the 40 t week; otherwise only where the rule
  // engine says a weekly hour limit is broken (48 t and the overtime caps), not past the 40 t normal week
  const red = young
    ? wk.hours > cap.week
    : wk.entries.some((e) => e.sev === 'd' && /^(weekly_over|week_over)/.test(e.code));
  const scale = Math.max(wk.hours, wk.norm ?? cap.week) || 1;
  // the week's other rule entries under the bar; the agreed week's overrun (weekly_over) is the bar's own
  // status, and the period's overtime is in «Mer om uka»
  const weekEntries = wk.entries.filter((e) => e.code !== 'year_over_200' && e.code !== 'weekly_over');

  const row = (x: StripDay) => {
    const s = x.shifts[0];
    const date = (
      <span className={css.dd}>
        {DN[dow(x.date)]}
        <b>{Number(x.date.slice(8, 10))}</b>
      </span>
    );
    const cls = `${css.row} ${x.date === today ? css.today : ''}`;
    const label = `${DNL[dow(x.date)]} ${fmtDs(x.date)}`;
    if (s) {
      const times = x.shifts.map((y) => shiftSpan(y.start_time, y.end_time)).join(', ');
      const h = x.shifts.reduce((a, y) => a + hours(y, who), 0);
      const note = x.shifts.map(noteOf).find(Boolean);
      const ask = askOn(s);
      const pend = !!ask || s.is_swap_requested || s.is_offer_requested;
      const sub = note
        ? note.text
        : ask?.other
          ? ask.swap
            ? T('Bytte med {name} venter', { name: ask.other.name })
            : T('{name} vil overta vakten', { name: ask.other.name })
          : pend
            ? T('Venter på svar')
            : [typeLabel(s.shiftType), T('{h} t', { h: num(r1(h)) })].filter(Boolean).join(', ');
      return (
        <button
          type="button"
          key={x.date}
          className={cls}
          aria-label={`${label}. ${times}. ${sub}`}
          data-testid="vperson-day-shift"
          onClick={() => openShift(s)}
        >
          {date}
          <span className={css.copy}>
            <strong>{times}</strong>
            <small className={note ? css[`s${note.sev}`] : ''}>{sub}</small>
          </span>
          <Icon name={pend ? 'swap' : 'chevronRight'} />
        </button>
      );
    }
    if (x.absence) {
      const a = x.absence;
      const name = a.absence_type?.name ?? T('Fravær');
      const sub = a.status === 'pending' ? T('Venter på svar') : T('Hele dagen');
      return (
        <button
          type="button"
          key={x.date}
          className={cls}
          aria-label={`${label}. ${name}. ${sub}`}
          data-testid="vperson-day-absence"
          onClick={() => onAbsence(a)}
        >
          {date}
          <span className={css.copy}>
            <strong>{name}</strong>
            <small>{sub}</small>
          </span>
          <Icon name="chevronRight" />
        </button>
      );
    }
    if (x.avail?.kind === 'unavail')
      return (
        <div key={x.date} className={cls} data-testid="vperson-day-no">
          {date}
          <span className={css.copy}>
            <strong>{T('Ikke tilgjengelig')}</strong>
            <small>{T('Hele dagen')}</small>
          </span>
          <span />
        </div>
      );
    const part = x.avail?.kind === 'partly' ? x.avail : null;
    const title = part
      ? T('Tilgjengelig {from} til {to}', { from: part.from, to: part.to })
      : T('Ingen vakt');
    const sub = part ? T('Ingen vakt planlagt') : T('Tilgjengelighet ikke avklart');
    const body = (
      <>
        {date}
        <span className={css.copy}>
          <strong>{title}</strong>
          <small>{sub}</small>
        </span>
      </>
    );
    return editable ? (
      <button
        type="button"
        key={x.date}
        className={`${cls} ${part ? css.can : ''}`}
        aria-label={`${T('Ny vakt {d}', { d: label })}. ${title}`}
        data-testid="vperson-day-free"
        onClick={() => onNew(x.date)}
      >
        {body}
        <Icon name="plus" />
      </button>
    ) : (
      <div key={x.date} className={`${cls} ${part ? css.can : ''}`} data-testid="vperson-day-free">
        {body}
        <span />
      </div>
    );
  };

  const fact = (k: string, v: React.ReactNode, id?: string) =>
    v == null || v === '' ? null : (
      <div key={k} data-testid={id}>
        <dt>{k}</dt>
        <dd>{v}</dd>
      </div>
    );
  const moreRows = [
    fair &&
      fact(
        T('Kvelder siste 4 uker'),
        <>
          <b>{fair.eve}</b>, {T('snitt {n}', { n: num(r1(fair.avgEve)) })}
        </>,
        'vperson-fair',
      ),
    fair &&
      fact(
        T('Helger siste 4 uker'),
        <>
          <b>{fair.wkd}</b>, {T('snitt {n}', { n: num(r1(fair.avgWkd)) })}
        </>,
        'vperson-fair',
      ),
    // the server counts overtime from the company's period start and sends the figure only past 200 t
    year &&
      fact(
        T('Overtid i perioden'),
        <>
          <b>{num(Number(year.args.hours))}</b> {T('av {n} t', { n: 200 })}
        </>,
        'vperson-overtime',
      ),
    young &&
      user.in_compulsory_school != null &&
      fact(T('Skoleplikt'), <b>{user.in_compulsory_school ? T('ja') : T('nei')}</b>),
    typeof vac === 'number' &&
      fact(
        T('Ferie igjen'),
        vacOf != null ? (
          <>
            <b>{num(vac)}</b> {T('av {n} dager', { n: num(vacOf) })}
          </>
        ) : (
          T('{n} dager', { n: num(vac) })
        ),
      ),
  ].filter(Boolean);

  const ef = full?.employment_form ?? user.employment_form;
  const form = ef
    ? { fast: T('Fast ansatt'), midlertidig: T('Midlertidig'), tilkalling: T('Tilkalling') }[ef]
    : null;
  const contract =
    user.contract_type === 'monthly' ? T('Fastlønn') : user.contract_type === 'hourly' ? T('Timelønn') : null;
  const rate =
    user.contract_type === 'monthly'
      ? full?.monthly_rate && T('{n} kr per måned', { n: num(Number(full.monthly_rate)) })
      : full?.hourly_rate && T('{n} kr per time', { n: num(Number(full.hourly_rate)) });
  const gaps = setupItems(full ?? user, !!full);
  const group = (title: string, rows: (React.ReactNode | null)[]) =>
    rows.some(Boolean) ? (
      <section className={css.group} data-testid="vperson-group">
        <h4>{title}</h4>
        <dl className={css.facts}>{rows}</dl>
      </section>
    ) : null;

  const profile = (
    <div className={css.body} data-testid="vperson-profileview">
      <h3 className={css.vt} ref={profHead} tabIndex={-1}>
        {T('Profil')}
      </h3>
      {gaps.length > 0 && (
        <p className={css.missing} data-testid="vperson-missing">
          <span>{T('Mangler {what}.', { what: gaps.map((g) => T(g.tag).toLowerCase()).join(', ') })}</span>
          {canEdit && (
            <Link className={css.tb} to={edit(gaps[0]?.field ?? 'name')}>
              {T('Legg til')}
            </Link>
          )}
        </p>
      )}
      {group(T('Arbeid'), [
        fact(T('Avdeling'), teams),
        fact(T('Stilling'), user.title),
        fact(T('Ansettelse'), form),
        fact(T('Avtalt per uke'), wk.norm != null ? T('{h} t', { h: num(wk.norm) }) : null),
        fact(T('Ansatt siden'), full?.employment_start ? fmtDate(full.employment_start) : null),
      ])}
      {canPay && group(T('Lønn'), [fact(T('Lønnsform'), contract), fact(T('Sats'), rate || null)])}
      {group(T('Saldo'), [
        fact(
          T('Ferie igjen'),
          typeof vac === 'number'
            ? vacOf != null
              ? T('{n} av {of} dager', { n: num(vac), of: num(vacOf) })
              : T('{n} dager', { n: num(vac) })
            : null,
        ),
        fact(T('Avspasering til gode'), bank?.agreed_on ? T('{h} t', { h: num(bank.balance, 1) }) : null),
        fact(T('Fleksitid'), bank?.flex ? T('{h} t', { h: num(bank.flex.hours, 1) }) : null),
      ])}
      {group(T('Person'), [
        fact(
          T('Fødselsdato'),
          birth ? (age != null ? `${fmtDate(birth)}, ${T('{n} år', { n: age })}` : fmtDate(birth)) : null,
        ),
        young && user.in_compulsory_school != null
          ? fact(T('Skoleplikt'), user.in_compulsory_school ? T('Ja') : T('Nei'))
          : null,
        fact(T('Ansattnummer'), full?.employee_id ?? user.employee_id),
      ])}
    </div>
  );

  const weekView = (
    <div className={css.body}>
      <section
        className={css.cap}
        data-testid="vperson-week"
        aria-label={T('Planlagte timer mot avtalt uke')}
      >
        <div className={css.capTop}>
          {/* «x av y» coloured by state, as the Vaktplan footer: amber over the agreed week, red past the legal
              limit (#272 A-U06) */}
          <strong className={red ? css.sd : over || under ? css.sw : ''} data-testid="vperson-hours">
            {wk.norm != null ? (
              <>
                {num(r1(wk.hours))} <span>{T('av {n} t', { n: num(wk.norm) })}</span>
              </>
            ) : (
              T('{h} t', { h: num(r1(wk.hours)) })
            )}
          </strong>
          <span className={red ? css.sd : over ? css.sw : ''} data-testid="vperson-status">
            {over
              ? T('{h} t over avtalen', { h: num(r1(wk.hours - (wk.norm as number))) })
              : T('Planlagt denne uka')}
          </span>
        </div>
        {/* over the agreed week the agreed part stays neutral and the overrun is its own part after a
            small gap; red only past the legal limit (owner 2026-10-28) */}
        <div
          className={css.meter}
          role="meter"
          aria-valuemin={0}
          aria-valuemax={scale}
          aria-valuenow={r1(wk.hours)}
          aria-label={T('Uka planlagt')}
        >
          {over ? (
            <>
              <i style={{ width: `${((wk.norm as number) / scale) * 100}%` }} />
              <i
                className={red ? css.fd : css.fw}
                style={{ width: `${((wk.hours - (wk.norm as number)) / scale) * 100}%` }}
                data-testid="vperson-over"
              />
            </>
          ) : (
            <i
              className={red ? css.fd : wk.norm != null && wk.hours >= wk.norm - 0.05 ? css.fo : ''}
              style={{ width: `${Math.min(100, (wk.hours / scale) * 100)}%` }}
            />
          )}
        </div>
        {weekEntries.map((e) => (
          <button
            key={e.code}
            type="button"
            className={`${css.wnote} ${css[`s${e.sev}`]}`}
            data-testid="vperson-weekrule"
            onClick={() => onRule(e)}
          >
            {ruleText(t, e)}
          </button>
        ))}
      </section>

      {asks.map((a) => (
        <div key={a.key} className={css.attention} data-testid="vperson-request">
          <p>
            <strong>
              {a.swap
                ? T('Bytteforespørsel på {dag}', { dag: DNL[dow(dateOf(a.shift as ApiShift))] as string })
                : T('Vil gi bort vakten på {dag}', { dag: DNL[dow(dateOf(a.shift as ApiShift))] as string })}
            </strong>
          </p>
          {a.other && (
            <p className={css.mut}>
              {a.swap
                ? T('{name} har sagt ja til å bytte.', { name: fullName(a.other) })
                : T('{name} har sagt ja til å overta vakten.', { name: fullName(a.other) })}
            </p>
          )}
          <button type="button" className={css.tb} onClick={() => openShift(a.shift as ApiShift)}>
            {T('Se forespørselen')}
            <Icon name="chevronRight" />
          </button>
        </div>
      ))}

      <section aria-labelledby="vperson-wk">
        <div className={css.st}>
          <h4 id="vperson-wk" ref={weekHead} tabIndex={-1}>
            {inWk ? T('Resten av uka') : T('Uke {n}', { n: isoWeek(week) })}
          </h4>
          <span>
            {inWk
              ? T('I dag er {dag}', { dag: DNL[dow(today)] as string })
              : T('{from} til {to}', { from: fmtDs(week), to: fmtDs(sun) })}
          </span>
        </div>
        <div className={css.days} data-testid="vperson-days">
          {ahead.map(row)}
        </div>
        {past.length > 0 && (
          <details className={css.past} data-testid="vperson-past">
            <summary>{pastLabel}</summary>
            <div className={css.days}>{past.map(row)}</div>
          </details>
        )}
      </section>

      {moreRows.length > 0 && (
        <details className={css.more} data-testid="vperson-more">
          <summary>{T('Mer om uka')}</summary>
          <dl className={css.facts}>{moreRows}</dl>
        </details>
      )}
    </div>
  );

  return (
    <aside className={`vpanel ${css.panel}`} aria-labelledby="vperson-t" data-testid="vperson">
      <div className={css.hd}>
        <div className={css.id}>
          <span className={`av ${avTone(user.id)} ${css.av}`} aria-hidden="true">
            {initials(fullName(user))}
          </span>
          <div className={css.who}>
            <h3 id="vperson-t" ref={head} tabIndex={-1}>
              {fullName(user)}
            </h3>
            <p data-testid="vperson-role">
              {role}
              {young && <span className={css.age}>{T('{n} år', { n: age as number })}</span>}
            </p>
          </div>
          <button type="button" className={`x ${css.x}`} aria-label={T('Lukk')} onClick={onClose}>
            <Icon name="close" />
          </button>
        </div>
        {((phone && touch) || canMessage || mail || canEdit) && (
          <div className={css.contact} data-testid="vperson-contact">
            {phone && touch && <CallButton phone={phone} name={user.name} />}
            {canMessage && <MessageButton id={user.id} name={user.name} />}
            {mail && (
              <a className="btn" href={`mailto:${mail}`}>
                <Icon name="mail" />
                {T('E-post')}
              </a>
            )}
            {canEdit && (
              <Link className="btn" to={edit('name')} data-testid="vperson-edit">
                <Icon name="pencil" />
                {T('Rediger')}
              </Link>
            )}
          </div>
        )}
        <div className={css.links}>
          {/* on a phone «Ring» calls and the number is said quietly; a PC cannot call, so the number
              itself is the button there and copies it (Contact.tsx) */}
          {phone &&
            (touch ? (
              <span className={css.phone}>{fmtPhone(phone)}</span>
            ) : (
              <CallButton phone={phone} name={user.name} quiet />
            ))}
          <button
            type="button"
            // a link in the app's link colour with its arrow, not grey text (#272 area C)
            className={`${css.tb} ${css.profile}`}
            disabled={view === 'profile'}
            onClick={() => setView('profile')}
            data-testid="vperson-openprofile"
          >
            {T('Åpne profil')}
            <Icon name="arrowRight" />
          </button>
        </div>
      </div>

      {view === 'profile' ? profile : weekView}

      <div className={css.foot}>
        {view === 'profile' ? (
          <>
            <Link className="btn pri" to={`/ansatte/${user.id}`} data-testid="vperson-profile">
              {T('Åpne hele profilen')}
            </Link>
            <button type="button" className={css.tb} onClick={() => setView('week')}>
              {T('Tilbake til uka')}
            </button>
          </>
        ) : (
          <>
            {primary && (
              <button type="button" className="btn pri" onClick={primary.go} data-testid="vperson-primary">
                <Icon name={primary.icon} />
                {primary.label}
              </button>
            )}
            {primary && 'plan' in primary ? (
              <button type="button" className={css.tb} onClick={toWeek} data-testid="vperson-other">
                {T('Velg en annen dag')}
              </button>
            ) : (
              <button type="button" className={css.tb} onClick={onOnly} data-testid="vperson-only">
                {T('Se alle vakter denne uka')}
              </button>
            )}
          </>
        )}
      </div>
    </aside>
  );
}

/** 2026-03-01 as 01.03.2026, as the profile writes dates. */
const fmtDate = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}`;
